import type { AuthoredGridPose, AuthoredGridSpan, Vec3 } from '../schema/index.ts';
import {
  FIXED_UNITS_PER_TILE,
  METERS_PER_FIXED_UNIT,
  type AuthoredPieceDescriptor,
  type AuthoredSpanTemplate,
  type FixedPoint,
} from './types.ts';
import { authoredForward, authoredRight, normalizeAuthoredHeadingStep } from './directions.ts';

function assertIntegerPoint(point: FixedPoint): FixedPoint {
  if (![point.x, point.y, point.z].every(Number.isInteger))
    throw new Error('Authored-grid geometry must use integer fixed units.');
  return point;
}

export function normalizeHeadingStep(step: number): number {
  return normalizeAuthoredHeadingStep(step);
}

export function fixedPointToWorld(point: FixedPoint): Vec3 {
  return {
    x: point.x * METERS_PER_FIXED_UNIT,
    y: point.y * METERS_PER_FIXED_UNIT,
    z: point.z * METERS_PER_FIXED_UNIT,
  };
}

export function worldToFixedPoint(point: Vec3): FixedPoint {
  return {
    x: Math.round(point.x / METERS_PER_FIXED_UNIT),
    y: Math.round(point.y / METERS_PER_FIXED_UNIT),
    z: Math.round(point.z / METERS_PER_FIXED_UNIT),
  };
}

export function snapWorldToGrid(point: Vec3): Vec3 {
  const fixed = worldToFixedPoint(point);
  return fixedPointToWorld({
    x: Math.round(fixed.x / FIXED_UNITS_PER_TILE) * FIXED_UNITS_PER_TILE,
    y: Math.round(fixed.y / FIXED_UNITS_PER_TILE) * FIXED_UNITS_PER_TILE,
    z: Math.round(fixed.z / FIXED_UNITS_PER_TILE) * FIXED_UNITS_PER_TILE,
  });
}

export function transformLocalPoint(entry: AuthoredGridPose, local: FixedPoint): FixedPoint {
  const forward = authoredForward(entry.headingStep);
  const right = authoredRight(entry.headingStep);
  return assertIntegerPoint({
    x: entry.x + local.x * forward.x + local.z * right.x,
    y: entry.y + local.y,
    z: entry.z + local.x * forward.z + local.z * right.z,
  });
}

export function descriptorExitPose(
  entry: AuthoredGridPose,
  descriptor: AuthoredPieceDescriptor,
): AuthoredGridPose {
  const last = descriptor.spans.at(-1);
  if (!last) throw new Error(`Descriptor ${descriptor.id} has no spans.`);
  const end = transformLocalPoint(entry, last.end);
  return {
    ...end,
    headingStep: normalizeHeadingStep(entry.headingStep + descriptor.exit.headingDelta),
    pitchDegrees: descriptor.exit.pitch === 'preserve' ? entry.pitchDegrees : descriptor.exit.pitch,
    bank: descriptor.exit.bank === 'preserve' ? entry.bank : descriptor.exit.bank,
    orientation:
      descriptor.exit.orientation === 'preserve' ? entry.orientation : descriptor.exit.orientation,
    windingQuarterTurns: entry.windingQuarterTurns + descriptor.exit.windingQuarterTurnsDelta,
  };
}

function bankDegrees(bank: AuthoredGridPose['bank']): number {
  if (bank === 'left') return -45;
  if (bank === 'right') return 45;
  return 0;
}

export function instantiateSpans(
  entry: AuthoredGridPose,
  descriptor: AuthoredPieceDescriptor,
): readonly AuthoredGridSpan[] {
  const defaultStart = bankDegrees(entry.bank);
  const exitPose = descriptorExitPose(entry, descriptor);
  const defaultEnd = bankDegrees(exitPose.bank);
  const entryWindingDegrees = entry.windingQuarterTurns * 90;
  return descriptor.spans.map((span: AuthoredSpanTemplate, index) => {
    const fractionStart = index / descriptor.spans.length;
    const fractionEnd = (index + 1) / descriptor.spans.length;
    return {
      end: transformLocalPoint(entry, span.end),
      control1: transformLocalPoint(entry, span.control1),
      control2: transformLocalPoint(entry, span.control2),
      bankStartDegrees:
        span.bankStartDegrees ?? defaultStart + (defaultEnd - defaultStart) * fractionStart,
      bankEndDegrees:
        span.bankEndDegrees ?? defaultStart + (defaultEnd - defaultStart) * fractionEnd,
      windingStartDegrees:
        span.windingStartDegrees === undefined
          ? entryWindingDegrees
          : entryWindingDegrees + span.windingStartDegrees,
      windingEndDegrees:
        span.windingEndDegrees === undefined
          ? (entry.windingQuarterTurns + descriptor.exit.windingQuarterTurnsDelta * fractionEnd) *
            90
          : entryWindingDegrees + span.windingEndDegrees,
    };
  });
}

export function posesEqual(first: AuthoredGridPose, second: AuthoredGridPose): boolean {
  return (
    first.x === second.x &&
    first.y === second.y &&
    first.z === second.z &&
    first.headingStep === second.headingStep &&
    first.pitchDegrees === second.pitchDegrees &&
    first.bank === second.bank &&
    first.orientation === second.orientation &&
    first.windingQuarterTurns === second.windingQuarterTurns
  );
}

/**
 * Physical connector equality keeps authored inversion winding unwrapped while
 * treating complete 360-degree turns as the same rail orientation.
 */
export function posesConnect(first: AuthoredGridPose, second: AuthoredGridPose): boolean {
  return (
    first.x === second.x &&
    first.y === second.y &&
    first.z === second.z &&
    first.headingStep === second.headingStep &&
    first.pitchDegrees === second.pitchDegrees &&
    first.bank === second.bank &&
    first.orientation === second.orientation &&
    (first.windingQuarterTurns - second.windingQuarterTurns) % 4 === 0
  );
}
